/**
 * The joined dropdown from the approved prototypes: a toggle button and a
 * panel. Single-select is a listbox of option buttons; multi-select is a
 * group of checkboxes. Arrow keys open and move, Home/End jump, Escape closes
 * and returns focus to the toggle, and a click outside closes it.
 */
import { h, uid } from "../dom";

export interface Option {
  value: string;
  label: string;
}

interface Base {
  /** Visible label above the control. */
  label: string;
  /** Hook for tests and styling, e.g. "year". */
  name: string;
}

function shell(base: Base, toggleText: string) {
  const panelId = uid(`${base.name}-panel`);
  const labelId = uid(`${base.name}-label`);
  const text = h("span", { class: "bsc-dropdown__label" }, toggleText);
  const toggle = h(
    "button",
    {
      type: "button",
      class: "bsc-dropdown__toggle",
      "aria-expanded": "false",
      "aria-controls": panelId,
      "aria-labelledby": `${labelId} ${panelId}-value`,
      "data-dropdown": base.name,
    },
    text,
    h("span", { class: "bsc-sr-only", id: `${panelId}-value` }, toggleText)
  );
  const panel = h("div", { class: "bsc-dropdown__panel", id: panelId, hidden: true });
  const root = h("div", { class: "bsc-dropdown" }, h("span", { class: "bsc-filters__label", id: labelId }, base.label), toggle, panel);

  const items = () => Array.from(panel.querySelectorAll<HTMLElement>("button, input"));
  const isOpen = () => toggle.getAttribute("aria-expanded") === "true";
  function setOpen(open: boolean) {
    toggle.setAttribute("aria-expanded", String(open));
    panel.hidden = !open;
    root.classList.toggle("is-open", open);
  }
  function close(refocus: boolean) {
    if (!isOpen()) return;
    setOpen(false);
    if (refocus) toggle.focus();
  }

  toggle.addEventListener("click", () => setOpen(!isOpen()));
  toggle.addEventListener("keydown", (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    setOpen(true);
    const list = items();
    (e.key === "ArrowDown" ? list[0] : list[list.length - 1])?.focus();
  });
  panel.addEventListener("keydown", (e) => {
    const list = items();
    const index = list.indexOf(document.activeElement as HTMLElement);
    let next = -1;
    if (e.key === "ArrowDown") next = (index + 1) % list.length;
    else if (e.key === "ArrowUp") next = (index - 1 + list.length) % list.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = list.length - 1;
    else if (e.key === "Escape") {
      e.preventDefault();
      close(true);
      return;
    } else return;
    e.preventDefault();
    list[next]?.focus();
  });
  root.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && isOpen()) close(true);
  });
  // One document listener per dropdown; it removes itself once the view that
  // owned the dropdown is gone.
  const outside = (e: Event) => {
    if (!root.isConnected) return document.removeEventListener("click", outside);
    if (!root.contains(e.target as Node)) close(false);
  };
  document.addEventListener("click", outside);

  function setToggleText(value: string) {
    text.textContent = value;
    const sr = toggle.querySelector(".bsc-sr-only");
    if (sr) sr.textContent = value;
  }
  return { root, panel, close, setToggleText };
}

export function singleSelect(
  base: Base & { options: Option[]; value: string; onChange: (value: string) => void }
): HTMLElement {
  const current = base.options.find((o) => o.value === base.value) ?? base.options[0];
  const { root, panel, close, setToggleText } = shell(base, current.label);
  panel.setAttribute("role", "listbox");
  panel.setAttribute("aria-label", base.label);
  for (const option of base.options) {
    panel.append(
      h(
        "button",
        {
          type: "button",
          role: "option",
          class: "bsc-dropdown__option",
          "aria-selected": String(option.value === current.value),
          "data-value": option.value,
          onClick: () => {
            panel.querySelectorAll('[role="option"]').forEach((el) =>
              el.setAttribute("aria-selected", String(el === panel.querySelector(`[data-value="${CSS.escape(option.value)}"]`)))
            );
            setToggleText(option.label);
            close(true);
            base.onChange(option.value);
          },
        },
        option.label
      )
    );
  }
  return root;
}

export function multiSelect(
  base: Base & { options: Option[]; values: string[]; onChange: (values: string[]) => void }
): HTMLElement {
  const summary = (values: string[]) =>
    values.length === base.options.length
      ? "All statuses"
      : values.length === 0
        ? "No statuses selected"
        : values.length === 1
          ? base.options.find((o) => o.value === values[0])!.label
          : `${values.length} statuses selected`;
  const { root, panel, setToggleText } = shell(base, summary(base.values));
  panel.setAttribute("role", "group");
  panel.setAttribute("aria-label", base.label);
  const read = () =>
    Array.from(panel.querySelectorAll<HTMLInputElement>("input:checked")).map((input) => input.value);
  for (const option of base.options) {
    panel.append(
      h(
        "label",
        { class: "bsc-dropdown__option" },
        h("input", {
          type: "checkbox",
          value: option.value,
          checked: base.values.includes(option.value),
          onChange: () => {
            const values = read();
            setToggleText(summary(values));
            base.onChange(values);
          },
        }),
        h("span", null, option.label)
      )
    );
  }
  return root;
}
